import { CalendarRange, MapPin, Stethoscope } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { ListVendorRequests } from '@cold-ambulance/client';
import { Badge, Skeleton, buttonVariants, cn } from '@cold-ambulance/shadcn';
import { formatDate, formatPaymentSchedule, LocationLink, sumEquipmentQuantities } from '@cold-ambulance/ui';

export function RequestCardSkeleton() {
  return (
    <div className="flex flex-col rounded-lg border bg-white p-6">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded-sm" />
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Skeleton className="h-5 w-5 rounded-sm" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Skeleton className="h-5 w-5 rounded-sm" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-5 w-12 rounded-md" />
          <Skeleton className="h-5 w-12 rounded-md" />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-5 w-24 rounded-full" />
        </div>
        <Skeleton className="h-10 w-24 rounded-md" />
      </div>
    </div>
  );
}

export function RequestCard({
  request,
}: {
  request: ListVendorRequests['records'][number];
}) {
  const { t } = useTranslation();
  const paymentKey = formatPaymentSchedule(request.paymentSchedule);
  const paymentLabel = paymentKey ? t(paymentKey) : null;

  const ambulanceCount =
    request.ambulanceServiceWithDriver.reduce(
      (acc, service) =>
        acc + service.type1Amount + service.type2Amount + service.type3Amount,
      0,
    ) +
    request.ambulanceServiceWithoutDriver.reduce(
      (acc, service) =>
        acc + service.type1Amount + service.type2Amount + service.type3Amount,
      0,
    ) +
    request.ambulance4x4WithDriver.reduce(
      (acc, service) => acc + service.amount,
      0,
    ) +
    request.ambulance4x4WithoutDriver.reduce(
      (acc, service) => acc + service.amount,
      0,
    );
  const mobileClinicCount = request.mobileClinicService.reduce(
    (acc, service) => acc + service.amount,
    0,
  );
  const paramedicCount = request.requestParamedicService.reduce(
    (acc, service) => acc + service.amount,
    0,
  );
  const nurseCount = request.requestNurseService.reduce(
    (acc, service) => acc + service.amount,
    0,
  );
  const doctorCount = request.doctorService.reduce(
    (acc, service) => acc + service.amount,
    0,
  );

  return (
    <div className="flex flex-col rounded-lg border bg-white p-6">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <MapPin size={20} />
          <p className="font-medium">{request.projectName}</p>
        </div>
        <LocationLink
          name={request.location.name}
          latitude={request.location.latitude}
          longitude={request.location.longitude}
          className="text-sm"
        />
        <div className="flex items-center gap-2 text-sm">
          <CalendarRange size={20} />
          {formatDate(request.scheduleStartDate)} -{' '}
          {formatDate(request.scheduleEndDate)}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Stethoscope size={20} />
          {ambulanceCount > 0 && (
            <>
              {ambulanceCount}
              <span>{t('services.ambulance')}</span>
            </>
          )}
          {(() => {
            const equipmentCount =
              request.ambulanceServiceWithDriver.reduce(
                (acc, service) =>
                  acc +
                  sumEquipmentQuantities(service.type1Equipment) +
                  sumEquipmentQuantities(service.type2Equipment) +
                  sumEquipmentQuantities(service.type3Equipment),
                0,
              ) +
              request.ambulanceServiceWithoutDriver.reduce(
                (acc, service) =>
                  acc +
                  sumEquipmentQuantities(service.type1Equipment) +
                  sumEquipmentQuantities(service.type2Equipment) +
                  sumEquipmentQuantities(service.type3Equipment),
                0,
              ) +
              request.ambulance4x4WithDriver.reduce(
                (acc, service) => acc + sumEquipmentQuantities(service.equipment),
                0,
              ) +
              request.ambulance4x4WithoutDriver.reduce(
                (acc, service) => acc + sumEquipmentQuantities(service.equipment),
                0,
              ) +
              request.mobileClinicService.reduce(
                (acc, service) => acc + sumEquipmentQuantities(service.equipment),
                0,
              ) +
              sumEquipmentQuantities(request.medicalEquipment as unknown[]);
            return equipmentCount > 0 ? (
              <span className="text-muted-foreground text-xs">
                ({equipmentCount} {t('services.equipment')})
              </span>
            ) : null;
          })()}
          {mobileClinicCount > 0 && (
            <span className="flex items-center gap-0.5 rounded-md border bg-white px-1 py-px">
              {mobileClinicCount}
              <span>{t('services.mobileClinic')}</span>
            </span>
          )}
          {paramedicCount > 0 && (
            <span className="flex items-center gap-0.5 rounded-md border bg-white px-1 py-px">
              {paramedicCount}
              <span>{t('services.paramedic')}</span>
            </span>
          )}
          {nurseCount > 0 && (
            <span className="flex items-center gap-0.5 rounded-md border bg-white px-1 py-px">
              {nurseCount}
              <span>{t('services.nurse')}</span>
            </span>
          )}
          {doctorCount > 0 && (
            <span className="flex items-center gap-0.5 rounded-md border bg-white px-1 py-px">
              {doctorCount}
              <span>{t('services.doctor')}</span>
            </span>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {request.proposalDeadline && (
            <Badge variant="secondary">
              {t('requests.proposalDeadline', { date: formatDate(request.proposalDeadline) })}
            </Badge>
          )}
          {request.bankGuaranteePercent && (
            <Badge variant="secondary">
              {t('requests.bankGuarantee', { percent: request.bankGuaranteePercent })}
            </Badge>
          )}
          {paymentLabel && <Badge variant="secondary">{paymentLabel}</Badge>}
        </div>
        <Link
          to={`/offers/place/${request.id}`}
          className={buttonVariants({
            className: cn('h-10 px-6'),
          })}
        >
          {t('requests.requestDetails')}
        </Link>
      </div>
    </div>
  );
}
